import useNotification from "../components/notifications/useNotifications";
import { ScanResult, useScannerOverride } from "../contexts/ScannerContext";
import useBaskets from "../data/useBaskets";
import i18n from "../i18n";
import { isPartScan, partIdFromScan } from "../utils/scanPartId";
import { looksLikeScan } from "../utils/till/exactTagMatch";

const t = (key: string) => i18n.t(key, { ns: "common" });

interface UseScanToBasketOptions {
  componentName: string;
  /** Called for scans that aren't part labels (vehicles, bins). The drawer navigates; the till warns. */
  onUnhandled?: (scan: ScanResult) => void;
  /** Called after a part was added, e.g. to clear a search box. */
  onAdded?: (partId: string) => void;
}

/**
 * While mounted, part-label scans add the part to the user's basket instead of navigating to
 * its stock page. Shared by the basket drawer and the till so both behave identically.
 */
export default function useScanToBasket({ componentName, onUnhandled, onAdded }: UseScanToBasketOptions): void {
  const { getBasket, addItem } = useBaskets(componentName);
  const { addNotification } = useNotification();

  useScannerOverride((scan) => {
    const { parts } = scan;

    // The provider fires on any quick keystroke burst, including a person typing into a search
    // box. Only a delimited label or a bare GUID is a barcode; anything else is just typing.
    if (!looksLikeScan(scan.raw)) return;

    if (isPartScan(parts)) {
      const scannedPartId = partIdFromScan(parts);

      if (!scannedPartId) {
        addNotification({
          variant: "warn",
          primaryText: t("Couldn't read part"),
          secondaryText: t("Scan a valid part barcode to add it to the basket."),
        });
        return;
      }

      const alreadyInBasket = getBasket.data?.data.items?.some(
        (item) => item.partId?.toLowerCase() === scannedPartId.toLowerCase()
      );
      if (alreadyInBasket) {
        addNotification({
          variant: "info",
          primaryText: t("Already in basket"),
          secondaryText: t("That part is already in the basket."),
        });
        return;
      }

      addItem.mutate(scannedPartId, {
        onSuccess: () => {
          addNotification({
            variant: "success",
            primaryText: t("Part added"),
            secondaryText: t("The scanned part was added to the basket."),
          });
          onAdded?.(scannedPartId);
        },
        onError: () => {
          addNotification({
            variant: "error",
            primaryText: t("Couldn't add part"),
            secondaryText: t("The scanned part could not be added to the basket."),
          });
        },
      });
      return;
    }

    onUnhandled?.(scan);
  });
}
